import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, Lock, Clock, Calendar, DollarSign, Loader2, Save, TrendingUp, Sparkles, Printer, BarChart3, Award, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import CashHoursPanel from "../components/CashHoursPanel";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MASTER_CODES = ["83528", "4817", "1111"];
// Program launches Monday 2026-06-22
const PROGRAM_START = "2026-06-22";

// US-style date helpers (MM/DD/YYYY)
const usDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${m}/${d}/${y}`;
};
const DEPT_COLORS = {
  "Admin": "#6b46c1",
  "Cashier": "#0ea5e9",
  "Deli": "#f59e0b",
  "Frozen-Dairy": "#06b6d4",
  "Frozen/Dairy": "#06b6d4",
  "Grocery": "#84cc16",
  "Meat & Fish": "#a61b1b",
  "Other": "#64748b",
  "Produce": "#22c55e",
};

const todayChicagoMonday = () => {
  const now = new Date();
  const ch = new Date(now.toLocaleString("en-US", { timeZone: "America/Chicago" }));
  const day = ch.getDay();
  const off = day === 0 ? -6 : 1 - day;
  ch.setDate(ch.getDate() + off);
  return ch.toISOString().slice(0, 10);
};
const addWeeks = (iso, n) => {
  const d = new Date(iso + "T12:00:00");
  d.setDate(d.getDate() + n * 7);
  return d.toISOString().slice(0, 10);
};
const isoMonth = (d) => d.toISOString().slice(0, 7);

// Period helpers
const getPeriodRange = (periodType, anchorIso) => {
  const anchor = new Date(anchorIso + "T12:00:00");
  if (periodType === "week") {
    const start = new Date(anchor);
    const day = start.getDay();
    start.setDate(start.getDate() + (day === 0 ? -6 : 1 - day));
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10), label: `Week of ${start.toLocaleDateString("en-US", { month: "short", day: "numeric" })}` };
  }
  if (periodType === "month") {
    const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10), label: start.toLocaleDateString("en-US", { month: "long", year: "numeric" }) };
  }
  if (periodType === "quarter") {
    const q = Math.floor(anchor.getMonth() / 3);
    const start = new Date(anchor.getFullYear(), q * 3, 1);
    const end = new Date(anchor.getFullYear(), q * 3 + 3, 0);
    return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10), label: `Q${q + 1} ${anchor.getFullYear()}` };
  }
  // year
  const start = new Date(anchor.getFullYear(), 0, 1);
  const end = new Date(anchor.getFullYear(), 11, 31);
  return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10), label: `${anchor.getFullYear()}` };
};

const MetricsPage = () => {
  const [loggedIn, setLoggedIn] = useState(() => localStorage.getItem("metrics_master_ok") === "1");
  const [pwd, setPwd] = useState("");
  const [periodType, setPeriodType] = useState("week"); // week | month | quarter | year
  const [anchorDate, setAnchorDate] = useState(todayChicagoMonday());
  const [rangeData, setRangeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [weeklyRevDraft, setWeeklyRevDraft] = useState("");
  const [savingRev, setSavingRev] = useState(false);

  // AI insights state
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insights, setInsights] = useState(null);

  // Weekly Revenue Report health metrics (POS-derived)
  const [healthData, setHealthData] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [aiBizInsights, setAiBizInsights] = useState(null);
  const [aiBizLoading, setAiBizLoading] = useState(false);

  const period = useMemo(() => getPeriodRange(periodType, anchorDate), [periodType, anchorDate]);

  const handleLogin = () => {
    if (MASTER_CODES.includes(pwd.trim())) {
      localStorage.setItem("metrics_master_ok", "1");
      setLoggedIn(true);
    } else {
      toast.error("Wrong code");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/schedule/metrics/range`, { params: { start: period.start, end: period.end } });
      setRangeData(res.data);
      // Find weekly revenue value for current week (if periodType=week)
      if (periodType === "week" && res.data.weekly_series?.length === 1) {
        setWeeklyRevDraft(res.data.weekly_series[0].revenue ? String(res.data.weekly_series[0].revenue) : "");
      } else {
        setWeeklyRevDraft("");
      }
    } catch (err) {
      toast.error("Failed to load metrics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (loggedIn) load(); /* eslint-disable-next-line */ }, [loggedIn, period.start, period.end]);

  // Load Weekly Report health metrics for the same period
  useEffect(() => {
    if (!loggedIn) return;
    let cancelled = false;
    (async () => {
      setHealthLoading(true);
      try {
        const r = await axios.get(`${API}/weekly-report/metrics/range`, {
          params: { range_type: "custom", start: period.start, end: period.end },
        });
        if (!cancelled) setHealthData(r.data);
      } catch (e) {
        if (!cancelled) setHealthData(null);
      } finally {
        if (!cancelled) setHealthLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loggedIn, period.start, period.end]);

  const generateBusinessInsights = async () => {
    setAiBizLoading(true);
    setAiBizInsights(null);
    try {
      const r = await axios.get(`${API}/weekly-report/insights/ai`, {
        params: { start: period.start, end: period.end },
      });
      setAiBizInsights(r.data);
    } catch (e) {
      toast.error("AI failed — " + (e.response?.data?.detail || e.message));
    } finally {
      setAiBizLoading(false);
    }
  };

  const saveWeeklyRevenue = async () => {
    if (periodType !== "week") return;
    setSavingRev(true);
    try {
      const total = parseFloat(weeklyRevDraft) || 0;
      await axios.post(`${API}/schedule/revenue`, { week_start: period.start, weekly_total: total });
      toast.success(`Saved $${total.toLocaleString()} for ${period.label}`);
      load();
    } catch (e) {
      toast.error("Failed to save revenue");
    } finally {
      setSavingRev(false);
    }
  };

  const generateInsights = async () => {
    if (periodType !== "week") {
      toast.error("AI insights work on weekly view. Switch to Week first.");
      return;
    }
    setInsightsLoading(true);
    setInsights(null);
    try {
      const prevWeek = addWeeks(period.start, -1);
      const res = await axios.post(`${API}/schedule/metrics/insights`, {
        week_start: period.start,
        compare_to: prevWeek,
      });
      setInsights(res.data);
    } catch (e) {
      toast.error("AI failed — " + (e.response?.data?.detail || e.message));
    } finally {
      setInsightsLoading(false);
    }
  };

  const handlePrint = () => window.print();

  // Derived data for charts
  const charts = useMemo(() => {
    if (!rangeData) return null;
    // Department bar chart
    const deptData = Object.entries(rangeData.totals.dept_breakdown || {})
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({ name, value: Math.round(value), fill: DEPT_COLORS[name] || "#64748b" }))
      .sort((a, b) => b.value - a.value);

    // Trend line (weeks)
    const trend = (rangeData.weekly_series || []).map(w => ({
      week: w.week_start.slice(5),
      labor: Math.round(w.labor_cost),
      revenue: Math.round(w.revenue),
      labor_pct: w.labor_pct,
    }));

    // Donut: department share %
    const totalLabor = deptData.reduce((s, d) => s + d.value, 0) || 1;
    const donut = deptData.map(d => ({ ...d, pct: ((d.value / totalLabor) * 100).toFixed(1) }));

    return { deptData, trend, donut, totalLabor };
  }, [rangeData]);

  // KPI deltas (compare last week if period=week)
  const compareDelta = useMemo(() => {
    if (!rangeData || periodType !== "week" || !rangeData.weekly_series?.[0]) return null;
    return rangeData.weekly_series[0];
  }, [rangeData, periodType]);

  // LOGIN
  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 w-full max-w-sm">
          <div className="w-16 h-16 bg-[#A61B1B] rounded-full flex items-center justify-center mx-auto mb-4"><Lock className="w-8 h-8 text-white" /></div>
          <h2 className="text-2xl font-bold text-center mb-1">Labor Metrics</h2>
          <p className="text-center text-sm text-[#666] mb-6">Manager access required</p>
          <Input type="password" placeholder="Master code" value={pwd} onChange={(e)=>setPwd(e.target.value)} onKeyDown={(e)=>e.key==="Enter"&&handleLogin()} className="mb-3" data-testid="metrics-master-pwd" />
          <Button onClick={handleLogin} className="w-full bg-[#A61B1B] h-12 text-lg" data-testid="metrics-login-btn">Continue</Button>
          <p className="text-center text-xs text-[#666] mt-4"><Link to="/timecard" className="hover:underline">← Back to PayPunch</Link></p>
        </div>
      </div>
    );
  }

  const totals = rangeData?.totals || { labor_cost: 0, revenue: 0, labor_pct: 0, dept_breakdown: {} };

  // Unified revenue: prefer manual weekly_revenue entry, fall back to POS-extracted net_sales.
  // This is what makes the top KPI tiles agree with the Health Metrics block below.
  const posNetSales = healthData?.totals?.net_sales || 0;
  const manualRevenue = totals.revenue || 0;
  const effectiveRevenue = manualRevenue > 0 ? manualRevenue : posNetSales;
  const revenueSource = manualRevenue > 0 ? "manual" : (posNetSales > 0 ? "pos-report" : "none");
  const effectiveLaborPct = effectiveRevenue > 0 ? +((totals.labor_cost / effectiveRevenue) * 100).toFixed(2) : 0;
  const healthPct = totals.labor_pct;
  const healthBand = healthPct === 0 ? "neutral" : healthPct < 22 ? "healthy" : healthPct < 28 ? "borderline" : "alarming";
  const healthColor = healthBand === "healthy" ? "#1A5E1F" : healthBand === "borderline" ? "#F59E0B" : healthBand === "alarming" ? "#A61B1B" : "#64748b";
  const healthEmoji = healthBand === "healthy" ? "✅" : healthBand === "borderline" ? "⚠️" : healthBand === "alarming" ? "🚨" : "📊";

  // Best/worst department
  const sortedDept = [...(charts?.deptData || [])].sort((a, b) => a.value - b.value);
  const leastSpender = sortedDept[0];
  const topSpender = sortedDept[sortedDept.length - 1];

  return (
    <div className="min-h-screen bg-stone-50 print:bg-white">
      <header className="bg-[#1A1A1A] text-white py-3 px-4 print:hidden">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center gap-2">
          <Link to="/timecard" className="text-white hover:text-[#D4AF37] flex items-center gap-2"><ArrowLeft className="w-5 h-5" /><span className="font-bold flex items-center gap-2"><TrendingUp className="w-5 h-5" /> Labor Metrics</span></Link>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" onClick={() => window.location.href = "/timecard"} variant="outline" className="border-white text-white hover:bg-white hover:text-[#1A1A1A] gap-1"><Clock className="w-4 h-4" />PayPunch</Button>
            <Button size="sm" onClick={() => window.location.href = "/schedule"} variant="outline" className="border-white text-white hover:bg-white hover:text-[#1A1A1A] gap-1"><Calendar className="w-4 h-4" />Schedule</Button>
            <Button size="sm" onClick={() => window.location.href = "/kiosk"} variant="outline" className="border-white text-white hover:bg-white hover:text-[#1A1A1A] gap-1"><Lock className="w-4 h-4" />Kiosk</Button>
            <Button size="sm" onClick={handlePrint} variant="outline" className="border-white text-white hover:bg-white hover:text-[#1A1A1A] gap-1" data-testid="metrics-print-btn"><Printer className="w-4 h-4" />Print</Button>
            <Button size="sm" variant="outline" onClick={() => { localStorage.removeItem("metrics_master_ok"); setLoggedIn(false); }} className="border-white text-white hover:bg-white hover:text-[#1A1A1A]">Logout</Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-3 print:p-0">
        {/* Period toolbar */}
        <div className="bg-white rounded-lg p-3 mb-3 flex flex-wrap items-center gap-2 print:hidden">
          <div className="flex rounded-md overflow-hidden border border-stone-300" data-testid="metrics-period-toggle">
            {["week","month","quarter","year"].map(p => (
              <button
                key={p}
                onClick={() => setPeriodType(p)}
                className={`px-3 py-1.5 text-sm font-semibold capitalize ${periodType === p ? "bg-[#A61B1B] text-white" : "bg-white text-stone-600 hover:bg-stone-100"}`}
                data-testid={`metrics-period-${p}`}
              >
                {p}
              </button>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={() => setAnchorDate(addWeeks(anchorDate, periodType === "week" ? -1 : periodType === "month" ? -4 : periodType === "quarter" ? -13 : -52))} data-testid="metrics-prev">← Prev</Button>
          <Button size="sm" variant="outline" onClick={() => setAnchorDate(todayChicagoMonday())}>Today</Button>
          <Button size="sm" variant="outline" onClick={() => setAnchorDate(addWeeks(anchorDate, periodType === "week" ? 1 : periodType === "month" ? 4 : periodType === "quarter" ? 13 : 52))} data-testid="metrics-next">Next →</Button>
          <span className="font-bold text-[#1A1A1A] ml-2" data-testid="metrics-period-label">{period.label}</span>
          <span className="text-xs text-stone-400">({usDate(period.start)} → {usDate(period.end)})</span>
        </div>

        {/* Print-only header */}
        <div className="hidden print:block mb-4 border-b-2 border-black pb-2">
          <h1 className="text-2xl font-black">Market Place on Oakton — Labor Metrics</h1>
          <p className="text-sm">{period.label} · {usDate(period.start)} → {usDate(period.end)}</p>
        </div>

        {/* Weekly Revenue Entry — only on Week view */}
        {periodType === "week" && (
          <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-3 mb-3 print:hidden" data-testid="weekly-revenue-card">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-amber-700" />
                <div>
                  <div className="font-bold text-amber-900">Enter this week's TOTAL revenue</div>
                  <div className="text-xs text-amber-700">Just one number — total sales for the entire week ({period.label})</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-amber-700 font-bold text-lg">$</span>
                <input
                  type="number"
                  inputMode="decimal"
                  value={weeklyRevDraft}
                  onChange={(e) => setWeeklyRevDraft(e.target.value)}
                  placeholder="35000"
                  className="border-2 border-amber-300 rounded-lg px-3 py-2 text-lg font-bold w-40"
                  data-testid="metrics-weekly-revenue-input"
                />
                <Button onClick={saveWeeklyRevenue} disabled={savingRev || !weeklyRevDraft} className="bg-amber-600 hover:bg-amber-700 gap-1" data-testid="metrics-save-weekly-revenue">
                  {savingRev ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save
                </Button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-16 text-stone-500"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading…</div>
        ) : !rangeData ? null : (
          <>
            {/* Cash Hours panel — manager enters cash labor here so Labor Spend includes it */}
            <div className="mb-3">
              <CashHoursPanel weekStart={period.start} onSaved={load} />
            </div>

            {/* BIG KPI strip — designed so a 3-year-old can read it */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
              <BigKPI
                label="Total Sales"
                emoji="💰"
                value={effectiveRevenue > 0 ? `$${Math.round(effectiveRevenue).toLocaleString()}` : "—"}
                color="#1A5E1F"
                testid="kpi-revenue"
                sublabel={revenueSource === "pos-report" ? "from POS report" : revenueSource === "manual" ? "manual entry" : "no report yet"}
              />
              <BigKPI
                label="Labor Spend"
                emoji="👥"
                value={`$${(totals.labor_cost || 0).toLocaleString()}`}
                color="#A61B1B"
                testid="kpi-labor"
                sublabel={totals.cash_labor?.cost > 0 ? `incl. $${totals.cash_labor.cost.toLocaleString()} cash (${totals.cash_labor.hours.toFixed(1)}h)` : null}
              />
              <BigKPI
                label="Labor %"
                emoji={effectiveLaborPct > 15 ? "🔴" : effectiveLaborPct > 12 ? "🟡" : effectiveLaborPct > 0 ? "🟢" : "💤"}
                value={effectiveRevenue > 0 ? `${effectiveLaborPct.toFixed(1)}%` : "—"}
                color={effectiveLaborPct > 15 ? "#A61B1B" : effectiveLaborPct > 12 ? "#92400E" : "#1A5E1F"}
                testid="kpi-labor-pct"
                sublabel={
                  effectiveRevenue === 0 ? "Enter revenue or upload POS report" :
                  effectiveLaborPct > 15 ? "HIGH — over 15%" :
                  effectiveLaborPct > 12 ? "WATCH — over 12%" :
                  "ON TARGET — under 12%"
                }
              />
              <BigKPI
                label="Take-Home"
                emoji="🏦"
                value={effectiveRevenue > 0 ? `$${Math.round(effectiveRevenue - totals.labor_cost).toLocaleString()}` : "—"}
                color="#1A5E1F"
                testid="kpi-takehome"
                sublabel="Sales − Labor"
              />
            </div>

            {/* ============================================================== */}
            {/* HEALTH METRICS — from Weekly Revenue Reports                    */}
            {/* ============================================================== */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-3" data-testid="health-metrics-panel">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <h2 className="font-bold text-lg text-stone-900 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#A61B1B]" /> Health Metrics
                  <span className="text-xs font-normal text-stone-500">({usDate(period.start)} → {usDate(period.end)})</span>
                </h2>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => window.location.href = "/weekly-report"} data-testid="add-weekly-report-btn">
                    + Add Weekly Report
                  </Button>
                  <Button size="sm" className="bg-purple-700 hover:bg-purple-800 gap-1" onClick={generateBusinessInsights} disabled={aiBizLoading || !healthData?.weeks_in_period} data-testid="biz-insights-btn">
                    {aiBizLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    AI Business Insights
                  </Button>
                </div>
              </div>
              {healthLoading ? (
                <div className="text-center py-6 text-stone-500"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</div>
              ) : !healthData || !healthData.weeks_in_period ? (
                <div className="text-center py-6 bg-amber-50 border border-amber-200 rounded">
                  <p className="text-amber-800 font-semibold">No weekly report submitted for this period yet.</p>
                  <p className="text-xs text-amber-700 mt-1">Click <b>+ Add Weekly Report</b> above to upload a photo of your POS Department Report or enter the numbers manually.</p>
                </div>
              ) : (
                <>
                  {/* Top-level health tiles (the circled numbers) */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
                    <HealthTile label="Net Sales" value={`$${(healthData.totals.net_sales || 0).toLocaleString()}`} />
                    <HealthTile label="Customers" value={(healthData.totals.customers || 0).toLocaleString()} />
                    <HealthTile label="Avg Sale" value={`$${(healthData.totals.avg_sale_per_customer || 0).toFixed(2)}`} sublabel="per customer" />
                    <HealthTile label="Items Sold" value={(healthData.totals.items_sold || 0).toLocaleString()} />
                    <HealthTile label="Avg Items" value={(healthData.totals.avg_items_per_customer || 0).toFixed(2)} sublabel="per customer" />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
                    <HealthTile label="Avg Item Price" value={`$${(healthData.totals.avg_item_price || 0).toFixed(2)}`} />
                    <HealthTile label="Returns" value={`$${(healthData.totals.returns || 0).toLocaleString()}`} subColor="#A61B1B" />
                    <HealthTile label="Cancels" value={`$${(healthData.totals.cancels || 0).toLocaleString()}`} subColor="#92400E" />
                    <HealthTile label="Discounts" value={`$${(healthData.totals.discounts || 0).toLocaleString()}`} />
                    <HealthTile label="Labor % Revenue" value={`${(healthData.totals.labor_pct_of_revenue || 0).toFixed(1)}%`} sublabel="target 8-12%" subColor={healthData.totals.labor_pct_of_revenue > 15 ? "#A61B1B" : healthData.totals.labor_pct_of_revenue > 12 ? "#F59E0B" : "#1A5E1F"} />
                  </div>

                  {/* Per-group revenue + labor breakdown */}
                  <h3 className="font-bold text-sm text-stone-700 mb-2 uppercase tracking-wide">Revenue vs Labor by Employee Group</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-stone-100">
                        <tr>
                          <th className="text-left px-3 py-2 font-semibold">Group</th>
                          <th className="text-right px-3 py-2 font-semibold">Revenue</th>
                          <th className="text-right px-3 py-2 font-semibold">% of Total</th>
                          <th className="text-right px-3 py-2 font-semibold">Labor (Direct)</th>
                          <th className="text-right px-3 py-2 font-semibold">+ Cashier/Admin Share</th>
                          <th className="text-right px-3 py-2 font-semibold">Total Labor</th>
                          <th className="text-right px-3 py-2 font-semibold">Labor %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(healthData.by_group || {}).map(([grp, v]) => {
                          const laborPct = v.labor_pct_of_revenue || 0;
                          const pctColor = laborPct > 15 ? "text-red-700 font-bold" : laborPct > 12 ? "text-amber-700 font-bold" : "text-green-700 font-bold";
                          return (
                            <tr key={grp} className="border-b border-stone-100">
                              <td className="px-3 py-2 font-bold text-xs uppercase">{grp}</td>
                              <td className="px-3 py-2 text-right font-mono">${(v.revenue || 0).toLocaleString()}</td>
                              <td className="px-3 py-2 text-right text-stone-600">{(v.revenue_pct_of_total || 0).toFixed(1)}%</td>
                              <td className="px-3 py-2 text-right font-mono">${(v.labor_direct || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                              <td className="px-3 py-2 text-right font-mono text-stone-500">${(v.labor_shared_alloc || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                              <td className="px-3 py-2 text-right font-mono font-bold">${(v.labor_total || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                              <td className={`px-3 py-2 text-right ${pctColor}`}>{laborPct.toFixed(1)}%</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-stone-500 mt-2">Cashier & Admin labor is distributed across groups proportional to each group's revenue share.</p>
                </>
              )}
            </div>

            {/* AI Business Insights panel — appears after user clicks button */}
            {aiBizInsights && (
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-lg p-4 mb-3" data-testid="biz-insights-panel">
                <h2 className="font-bold text-lg text-purple-900 mb-2 flex items-center gap-2">
                  <Sparkles className="w-5 h-5" /> AI Business Insights ({aiBizInsights.weeks_analyzed} weeks analyzed)
                </h2>
                <p className="text-stone-800 text-sm mb-3 leading-relaxed font-medium">{aiBizInsights.summary}</p>
                {aiBizInsights.trends && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
                    <InsightTile label="Revenue" value={aiBizInsights.trends.revenue_direction} sub={`${aiBizInsights.trends.revenue_change_pct?.toFixed(1) || 0}%`} />
                    <InsightTile label="Strongest" value={aiBizInsights.trends.strongest_dept} />
                    <InsightTile label="Weakest" value={aiBizInsights.trends.weakest_dept} />
                    <InsightTile label="Customers" value={aiBizInsights.trends.customer_trend} />
                    <InsightTile label="Basket Size" value={aiBizInsights.trends.basket_size_trend} />
                  </div>
                )}
                {aiBizInsights.recommendations?.length > 0 && (
                  <div className="mb-3">
                    <h3 className="font-bold text-sm text-purple-900 mb-1">🎯 Recommendations</h3>
                    <ul className="space-y-1.5">
                      {aiBizInsights.recommendations.map((r, i) => (
                        <li key={i} className="bg-white/70 rounded p-2 text-sm">
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold mr-2 ${r.priority === "HIGH" ? "bg-red-200 text-red-900" : r.priority === "MEDIUM" ? "bg-amber-200 text-amber-900" : "bg-stone-200 text-stone-700"}`}>{r.priority}</span>
                          <span className="text-stone-500 text-xs mr-2">[{r.area}]</span>
                          <span className="font-medium">{r.action}</span>
                          {r.impact && <div className="text-xs text-stone-600 mt-0.5 ml-12">→ {r.impact}</div>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {aiBizInsights.wins?.length > 0 && (
                    <div className="bg-green-50 rounded p-2">
                      <div className="font-bold text-xs text-green-900 mb-1">✅ WINS</div>
                      {aiBizInsights.wins.map((w, i) => <div key={i} className="text-sm text-green-800">• {w}</div>)}
                    </div>
                  )}
                  {aiBizInsights.risks?.length > 0 && (
                    <div className="bg-red-50 rounded p-2">
                      <div className="font-bold text-xs text-red-900 mb-1">⚠️ RISKS</div>
                      {aiBizInsights.risks.map((w, i) => <div key={i} className="text-sm text-red-800">• {w}</div>)}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Best / Worst department callouts */}
            {charts?.deptData?.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-3" data-testid="best-dept-callout">
                  <div className="flex items-center gap-2"><Award className="w-5 h-5 text-green-700" /><span className="font-bold text-green-900">🏆 Most Efficient Dept</span></div>
                  <div className="mt-2 text-2xl font-black" style={{ color: DEPT_COLORS[leastSpender?.name] }}>{leastSpender?.name}</div>
                  <div className="text-sm text-green-800">Uses just <b>${leastSpender?.value.toLocaleString()}</b> in labor</div>
                </div>
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-3" data-testid="worst-dept-callout">
                  <div className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-700" /><span className="font-bold text-red-900">⚠️ Biggest Spender</span></div>
                  <div className="mt-2 text-2xl font-black" style={{ color: DEPT_COLORS[topSpender?.name] }}>{topSpender?.name}</div>
                  <div className="text-sm text-red-800">Spent <b>${topSpender?.value.toLocaleString()}</b> on labor ({((topSpender?.value / charts.totalLabor) * 100).toFixed(0)}% of total)</div>
                </div>
              </div>
            )}

            {/* AI Insights panel */}
            {periodType === "week" && (
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-purple-200 rounded-lg p-4 mb-3" data-testid="ai-insights-card">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <h2 className="font-bold text-lg text-purple-900 flex items-center gap-2">
                    <Sparkles className="w-5 h-5" /> Smart Insights
                  </h2>
                  <Button onClick={generateInsights} disabled={insightsLoading} className="bg-purple-700 hover:bg-purple-800 gap-1" data-testid="generate-insights-btn">
                    {insightsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {insights ? "Regenerate" : "Generate AI Report"}
                  </Button>
                </div>
                {insights ? (
                  <div className="bg-white rounded-lg p-4 prose prose-sm max-w-none whitespace-pre-wrap text-stone-800" data-testid="insights-content">
                    {insights.insights}
                  </div>
                ) : (
                  <div className="text-sm text-purple-700">Click "Generate AI Report" — Gemini will analyze this week vs last week and give you a plain-English summary in about 5 seconds.</div>
                )}
              </div>
            )}

            {/* CHARTS */}
            {charts && charts.deptData.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
                {/* Department bar */}
                <div className="bg-white rounded-lg p-3 print:break-inside-avoid" data-testid="chart-dept-bar">
                  <h3 className="font-bold mb-2 flex items-center gap-2"><BarChart3 className="w-4 h-4" />Labor $ by Department</h3>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={charts.deptData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                        {charts.deptData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Department share donut */}
                <div className="bg-white rounded-lg p-3 print:break-inside-avoid" data-testid="chart-dept-pie">
                  <h3 className="font-bold mb-2 flex items-center gap-2"><BarChart3 className="w-4 h-4" />Department Share</h3>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={charts.donut} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90} label={(entry) => `${entry.pct}%`}>
                        {charts.donut.map((d, i) => <Cell key={i} fill={d.fill} />)}
                      </Pie>
                      <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Trend line (only meaningful for multi-week ranges) */}
                {charts.trend.length > 1 && (
                  <div className="bg-white rounded-lg p-3 lg:col-span-2 print:break-inside-avoid" data-testid="chart-trend-line">
                    <h3 className="font-bold mb-2 flex items-center gap-2"><TrendingUp className="w-4 h-4" />Labor & Revenue Trend</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={charts.trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                        <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Line yAxisId="left" type="monotone" dataKey="revenue" stroke="#1A5E1F" strokeWidth={3} name="Revenue ($)" dot={{ r: 4 }} />
                        <Line yAxisId="left" type="monotone" dataKey="labor" stroke="#A61B1B" strokeWidth={3} name="Labor ($)" dot={{ r: 4 }} />
                        <Line yAxisId="right" type="monotone" dataKey="labor_pct" stroke="#F59E0B" strokeWidth={2} strokeDasharray="5 5" name="Labor %" dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}

            {/* Department breakdown table */}
            {charts && charts.deptData.length > 0 && (
              <div className="bg-white rounded-lg p-3 mb-3 print:break-inside-avoid" data-testid="metrics-dept-table">
                <h3 className="font-bold mb-2">Department Breakdown ({period.label})</h3>
                <table className="w-full text-sm">
                  <thead className="text-left text-stone-500 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="py-1">Department</th>
                      <th className="py-1 text-right">Labor $</th>
                      <th className="py-1 text-right">% of Total</th>
                      <th className="py-1 text-right">vs Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {charts.donut.map((d) => {
                      const pctOfRev = totals.revenue > 0 ? ((d.value / totals.revenue) * 100).toFixed(1) : "—";
                      return (
                        <tr key={d.name} className="border-t">
                          <td className="py-2 font-semibold flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full inline-block" style={{ background: d.fill }} />
                            {d.name}
                          </td>
                          <td className="py-2 text-right font-bold">${d.value.toLocaleString()}</td>
                          <td className="py-2 text-right">{d.pct}%</td>
                          <td className="py-2 text-right text-stone-600">{pctOfRev}%</td>
                        </tr>
                      );
                    })}
                    <tr className="border-t bg-stone-100 font-bold">
                      <td className="py-2">TOTAL</td>
                      <td className="py-2 text-right">${charts.totalLabor.toLocaleString()}</td>
                      <td className="py-2 text-right">100%</td>
                      <td className="py-2 text-right">{totals.revenue > 0 ? `${healthPct}%` : "—"}</td>
                    </tr>
                  </tbody>
                </table>
                <p className="text-xs text-stone-500 mt-2">
                  💡 <b>How to read</b>: "% of Total" shows what share of your labor budget that dept used. "vs Revenue" shows that dept's labor as a % of all your sales (lower = more efficient).
                </p>
              </div>
            )}

            {/* Week-by-week mini table (for month/quarter/year views) */}
            {periodType !== "week" && rangeData.weekly_series?.length > 0 && (
              <div className="bg-white rounded-lg p-3 mb-3 print:break-inside-avoid">
                <h3 className="font-bold mb-2">Week-by-Week Breakdown</h3>
                <table className="w-full text-sm">
                  <thead className="text-left text-stone-500 text-xs uppercase tracking-wider">
                    <tr><th className="py-1">Week of</th><th className="py-1 text-right">Revenue</th><th className="py-1 text-right">Labor</th><th className="py-1 text-right">Labor %</th></tr>
                  </thead>
                  <tbody>
                    {rangeData.weekly_series.map(w => (
                      <tr key={w.week_start} className="border-t">
                        <td className="py-1.5">{usDate(w.week_start)}</td>
                        <td className="py-1.5 text-right">${w.revenue.toLocaleString()}</td>
                        <td className="py-1.5 text-right">${w.labor_cost.toLocaleString()}</td>
                        <td className={`py-1.5 text-right font-semibold ${w.labor_pct > 28 ? "text-red-600" : w.labor_pct > 22 ? "text-amber-600" : "text-green-700"}`}>{w.labor_pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* No data hint */}
            {(!charts || charts.deptData.length === 0) && totals.labor_cost === 0 && periodType === "week" && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
                <BarChart3 className="w-10 h-10 mx-auto mb-2 text-amber-600" />
                <p className="font-bold text-stone-900 text-base">No schedule built for this week yet</p>
                <p className="text-sm mt-1 text-stone-600">Carry forward the previous week's schedule in one click — your staff usually keeps the same shifts week-over-week.</p>
                <div className="flex flex-wrap gap-2 justify-center mt-4">
                  <Button
                    className="bg-[#A61B1B] hover:bg-[#841515] text-white"
                    onClick={async () => {
                      const target = period.start;
                      try {
                        const r = await axios.post(`${API}/schedule/copy-week`, null, { params: { target_week_start: target } });
                        toast.success(`Copied ${r.data.copied || 0} shifts forward`);
                        setTimeout(() => window.location.reload(), 800);
                      } catch (err) {
                        toast.error(`Copy failed: ${err.response?.data?.detail || err.message}`);
                      }
                    }}
                    data-testid="btn-copy-prev-week-from-metrics"
                  >
                    📋 Copy Last Week's Schedule
                  </Button>
                  <Button variant="outline" onClick={() => window.location.href = `/schedule?week=${period.start}`} data-testid="btn-open-schedule-from-metrics">
                    Open Schedule Builder
                  </Button>
                </div>
              </div>
            )}
            {(!charts || charts.deptData.length === 0) && totals.labor_cost === 0 && periodType !== "week" && (
              <div className="bg-stone-100 rounded-lg p-6 text-center text-stone-500">
                <BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="font-semibold">No schedule data for this {periodType} yet</p>
                <p className="text-sm mt-1">Build a schedule in <Link to="/schedule" className="underline text-[#A61B1B]">Schedule Builder</Link> and the charts will appear automatically.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const BigKPI = ({ label, emoji, value, color, sublabel, testid }) => (
  <div className="bg-white rounded-2xl p-5 shadow-sm border-2 border-stone-100 print:border-stone-300" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs uppercase tracking-wider font-semibold text-stone-500">{label}</span>
      <span className="text-2xl">{emoji}</span>
    </div>
    <div className="text-4xl font-black" style={{ color }}>{value}</div>
    {sublabel && <div className="text-xs mt-1 font-semibold uppercase tracking-wider" style={{ color }}>{sublabel}</div>}
  </div>
);

const HealthTile = ({ label, value, sublabel, subColor }) => (
  <div className="bg-stone-50 border border-stone-200 rounded-lg p-2.5">
    <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">{label}</div>
    <div className="text-lg font-extrabold text-stone-900 mt-0.5" style={subColor ? { color: subColor } : undefined}>{value}</div>
    {sublabel && <div className="text-[10px] text-stone-500 mt-0.5">{sublabel}</div>}
  </div>
);

const InsightTile = ({ label, value, sub }) => (
  <div className="bg-white/80 rounded p-2 border border-purple-100">
    <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">{label}</div>
    <div className="text-base font-bold text-purple-900 capitalize">{value || "—"}</div>
    {sub && <div className="text-xs text-stone-600">{sub}</div>}
  </div>
);

export default MetricsPage;
